import { STATUS } from "@/redux/constant";
import { resetPromotion } from "@/redux/reducers/payment";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
	FormControl,
	IconButton,
	InputAdornment,
	OutlinedInput,
	ThemeProvider,
	createTheme,
} from "@mui/material";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

const theme = createTheme({
	components: {
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					width: "100%",
					height: "56px",
					borderRadius: "12px",
					".MuiOutlinedInput-notchedOutline": {
						borderColor: "#71717A",
					},
					"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
						borderColor: "#0072CE",
					},
				},
			},
		},
		MuiFormControl: {
			styleOverrides: {
				root: {
					width: "100%",
				},
			},
		},
		MuiInputAdornment: {
			styleOverrides: {
				root: {
					color: "#71717A",
					fontFamily: "Montserrat",
					fontSize: "14px",
					fontWeight: 600,
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				root: {
					padding: "0 8px",
					fontFamily: "Montserrat",
					fontSize: "12px",
					backgroundColor: "transparent",
				},
			},
		},
	},
});

type Props = {
	value?: string;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onClear?: () => void;
	onSubmit: (code: string) => void;
	disable?: boolean;
};

const PromoCode: React.FC<Props> = ({
	value,
	onChange,
	onClear,
	onSubmit,
	disable,
}) => {
	const [inputValue, setInputValue] = useState(value || "");

	const dispatch = useDispatch();
	const promotion = useSelector((state: any) => state.payment?.promotion);
	const error = promotion.status === STATUS.FAILED;
	const success = promotion.status === STATUS.SUCCESS;

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setInputValue(e.target.value);
		onChange?.(e);
	};

	const handleClearInput = () => {
		setInputValue("");
		onChange?.({
			target: { value: "" },
		} as React.ChangeEvent<HTMLInputElement>);
		dispatch(resetPromotion());
		onClear?.();
	};

	const handleSubmit = () => {
		onSubmit(inputValue);
	};

	return (
		<ThemeProvider theme={theme}>
			<FormControl>
				<OutlinedInput
					className={`${error ? "border-danger-300" : ""} ${success ? "!bg-success-100 !border-success-200" : ""}`}
					value={inputValue}
					onChange={handleInputChange}
					placeholder="Enter promo code"
					type="text"
					disabled={disable}
					error={error}
					endAdornment={
						<InputAdornment position="end">
							{success ? (
								<IconButton onClick={handleClearInput}>
									<CloseIcon />
								</IconButton>
							) : error ? (
								<>
									<IconButton onClick={handleClearInput}>
										<CloseIcon />
									</IconButton>
									<div className="border-l border-solid border-gray-200 pl-2">
										<button
											type="button"
											className="text-primary"
											onClick={handleSubmit}
										>
											APPLY
										</button>
									</div>
								</>
							) : (
								<button
									type="button"
									className="cursor-pointer"
									disabled={disable}
									onClick={handleSubmit}
								>
									APPLY
								</button>
							)}
						</InputAdornment>
					}
				/>
				{disable && (
					<div className="flex items-center gap-2 py-1 px-2">
						<ErrorOutlineIcon className="!text-sm text-primary" />
						<span className="text-sm text-primary">
							Please select a package before applying the discount.
						</span>
					</div>
				)}
				{error && (
					<div className="flex items-center gap-2 py-1 px-2">
						<CancelIcon className="!text-sm text-danger-300" />
						<span className="text-sm text-danger-300">
							{promotion.error?.message}
						</span>
					</div>
				)}
				{success && (
					<div className="flex items-center gap-2 py-1 px-2">
						<CheckCircleIcon className="!text-sm text-success-200" />
						<span className="text-sm text-success-200">
							{promotion.data?.message}
						</span>
					</div>
				)}
			</FormControl>
		</ThemeProvider>
	);
};

export default PromoCode;
